import { useState } from 'react';

const terms = [
  // Basics
  { term: 'Blockchain', category: 'Basics', def: 'A distributed ledger of transactions grouped into blocks, chained together cryptographically. No single entity controls it.' },
  { term: 'Wallet', category: 'Basics', def: 'Software (or hardware) that stores your private keys and lets you send/receive crypto. It doesn\'t hold coins — it holds access.' },
  { term: 'Private Key', category: 'Basics', def: 'A secret 256-bit number that proves ownership of funds. Anyone who has it controls your assets. Never share it.' },
  { term: 'Public Key', category: 'Basics', def: 'Derived from your private key — safe to share. Used to generate your wallet address and verify signatures.' },
  { term: 'Gas', category: 'Basics', def: 'Fee paid to compensate network validators for the computation required to process a transaction, denominated in Gwei on Ethereum.' },

  // DeFi
  { term: 'DeFi', category: 'DeFi', def: 'Decentralized Finance — financial services (lending, trading, yield) built on smart contracts with no banks or intermediaries.' },
  { term: 'Liquidity Pool', category: 'DeFi', def: 'A smart contract holding two tokens that powers a DEX. Traders swap against it; LPs earn a share of fees.' },
  { term: 'Yield Farming', category: 'DeFi', def: 'Depositing assets into DeFi protocols to earn rewards — often a mix of trading fees and governance tokens.' },
  { term: 'Impermanent Loss', category: 'DeFi', def: 'The temporary loss LP providers experience when token prices diverge from the ratio at deposit time.' },
  { term: 'TVL', category: 'DeFi', def: 'Total Value Locked — the dollar value of all assets deposited in a DeFi protocol. A common proxy for its adoption.' },

  // Trading
  { term: 'HODL', category: 'Trading', def: 'Holding through volatility instead of selling. Originated from a 2013 typo of "hold" that became a meme.' },
  { term: 'Altcoin', category: 'Trading', def: 'Any cryptocurrency other than Bitcoin. Ranges from Ethereum to memecoins.' },
  { term: 'Whale', category: 'Trading', def: 'An entity holding enough of an asset to move its price when buying or selling. Whale wallets are closely watched.' },
  { term: 'FUD', category: 'Trading', def: 'Fear, Uncertainty, Doubt — negative sentiment (real or manufactured) that causes panic selling.' },
  { term: 'ATH', category: 'Trading', def: 'All-Time High — the highest price an asset has ever traded at.' },
];

const categories = ['All', 'Basics', 'DeFi', 'Trading'];

const categoryColors = {
  Basics:  { bg: '#1e293b', text: '#7dd3fc', border: '#0ea5e9' },
  DeFi:    { bg: '#1a1f2e', text: '#a78bfa', border: '#7c3aed' },
  Trading: { bg: '#1f1e1a', text: '#fcd34d', border: '#d97706' },
};

export default function CryptoDictionary() {
  const [activeCategory, setActiveCategory] = useState('All');
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState(null);

  const filtered = terms.filter((t) => {
    const matchCat = activeCategory === 'All' || t.category === activeCategory;
    const matchSearch =
      t.term.toLowerCase().includes(search.toLowerCase()) ||
      t.def.toLowerCase().includes(search.toLowerCase());
    return matchCat && matchSearch;
  });

  return (
    <div style={s.page}>
      {/* ── HEADER ── */}
      <div style={s.header}>
        <div style={s.logo}>₿</div>
        <h1 style={s.title}>Crypto Dictionary</h1>
        <p style={s.subtitle}>Essential terms every crypto participant should know</p>
      </div>

      {/* ── CONTROLS ── */}
      <div style={s.controls}>
        <input
          style={s.search}
          placeholder="Search terms..."
          value={search}
          onChange={(e) => { setSearch(e.target.value); setExpanded(null); }}
        />
        <div style={s.tabs}>
          {categories.map((cat) => (
            <button
              key={cat}
              style={{ ...s.tab, ...(activeCategory === cat ? s.tabActive : {}) }}
              onClick={() => { setActiveCategory(cat); setExpanded(null); }}
            >
              {cat}
            </button>
          ))}
        </div>
      </div>

      {/* ── TERMS LIST ── */}
      <div style={s.list}>
        {filtered.length === 0 && (
          <p style={s.empty}>No terms found for "{search}"</p>
        )}
        {filtered.map((item) => {
          const col = categoryColors[item.category];
          const isOpen = expanded === item.term;
          return (
            <div
              key={item.term}
              style={{
                ...s.card,
                borderLeft: `3px solid ${col.border}`,
                background: isOpen ? '#1a1a2e' : '#141414',
              }}
              onClick={() => setExpanded(isOpen ? null : item.term)}
            >
              <div style={s.cardHeader}>
                <div style={s.cardLeft}>
                  <span style={{ ...s.badge, background: col.bg, color: col.text }}>
                    {item.category}
                  </span>
                  <span style={s.termName}>{item.term}</span>
                </div>
                <span style={{ ...s.chevron, transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)' }}>
                  ▾
                </span>
              </div>
              {isOpen && <p style={s.def}>{item.def}</p>}
            </div>
          );
        })}
      </div>

      {/* ── FOOTER ── */}
      <p style={s.footer}>{terms.length} terms · click any card to expand</p>
    </div>
  );
}

// ── STYLES ──────────────────────────────────────────────────
const s = {
  page: {
    minHeight: '100vh',
    background: '#0d0d0d',
    color: '#e2e8f0',
    fontFamily: '"Inter", "Segoe UI", sans-serif',
    padding: '40px 16px 60px',
    boxSizing: 'border-box',
  },

  // Header
  header: { textAlign: 'center', marginBottom: 36 },
  logo: {
    fontSize: 48,
    background: 'linear-gradient(135deg, #f59e0b, #ef4444)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    display: 'inline-block',
    marginBottom: 8,
  },
  title: { fontSize: 32, fontWeight: 800, margin: '0 0 8px', letterSpacing: '-0.5px' },
  subtitle: { color: '#64748b', fontSize: 15, margin: 0 },

  // Controls
  controls: {
    maxWidth: 640,
    margin: '0 auto 28px',
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
  },
  search: {
    width: '100%',
    padding: '11px 16px',
    background: '#1a1a1a',
    border: '1px solid #2a2a2a',
    borderRadius: 10,
    color: '#e2e8f0',
    fontSize: 14,
    outline: 'none',
    boxSizing: 'border-box',
    transition: 'border-color 0.2s',
  },
  tabs: { display: 'flex', gap: 8, flexWrap: 'wrap' },
  tab: {
    padding: '7px 18px',
    background: '#1a1a1a',
    border: '1px solid #2a2a2a',
    borderRadius: 999,
    color: '#64748b',
    fontSize: 13,
    fontWeight: 500,
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  tabActive: {
    background: '#f59e0b22',
    border: '1px solid #f59e0b',
    color: '#f59e0b',
  },

  // List
  list: { maxWidth: 640, margin: '0 auto', display: 'flex', flexDirection: 'column', gap: 8 },
  empty: { color: '#475569', textAlign: 'center', padding: '32px 0', fontSize: 14 },

  // Card
  card: {
    borderRadius: 10,
    padding: '14px 18px',
    cursor: 'pointer',
    transition: 'background 0.2s',
    userSelect: 'none',
  },
  cardHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  cardLeft: { display: 'flex', alignItems: 'center', gap: 10 },
  badge: {
    fontSize: 11,
    fontWeight: 600,
    padding: '3px 9px',
    borderRadius: 999,
    letterSpacing: '0.4px',
    textTransform: 'uppercase',
  },
  termName: { fontSize: 15, fontWeight: 600, color: '#f1f5f9' },
  chevron: { color: '#475569', fontSize: 18, transition: 'transform 0.25s', lineHeight: 1 },
  def: {
    marginTop: 10,
    marginBottom: 2,
    color: '#94a3b8',
    fontSize: 14,
    lineHeight: 1.65,
    paddingLeft: 2,
  },

  // Footer
  footer: { textAlign: 'center', color: '#334155', fontSize: 12, marginTop: 32 },
};